'use client';

import { useEffect, useRef } from 'react';
import { cn } from '@/app/lib/utils';
import { Typography, type TypographyVariant } from '../Typography';
import { ButtonIcon } from '@/app/common';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  titleVariant?: TypographyVariant;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  ariaLabel?: string;
  headerClassName?: string;
  closeButtonClassName?: string;
  closeButtonAriaLabel?: string;
  /** Max-width Tailwind class, e.g. 'max-w-lg'. Defaults to 'max-w-lg'. */
  maxWidth?: string;
}

function ModalBody({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex-1 overflow-y-auto flex flex-col p-4 md:px-8 pt-2 md:pt-4', className)}>{children}</div>
  );
}

function ModalFooter({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex-shrink-0',
        'p-4 md:p-8 md:pt-0',
        'border-t border-secondary-100 md:border-0',
        'bg-secondary-50',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Modal({
  isOpen,
  onClose,
  title,
  titleVariant = 'h4',
  subtitle,
  children,
  className,
  ariaLabel,
  headerClassName,
  closeButtonClassName,
  closeButtonAriaLabel = 'Dismiss notice',
  maxWidth = 'max-w-lg',
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen) {
      if (!dialog.open) dialog.showModal();
      document.body.classList.add('overflow-hidden');
    } else {
      if (dialog.open) dialog.close();
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      aria-label={ariaLabel}
      onClick={handleBackdropClick}
      onClose={onClose}
      className={cn(
        'fixed inset-0 md:inset-auto md:top-1/2 md:left-1/2',
        'md:-translate-x-1/2 md:-translate-y-1/2',
        'flex flex-col [&:not([open])]:hidden',
        'h-dvh w-full max-h-none md:h-auto md:max-h-[90vh]',
        'p-0',
        'bg-secondary-50',
        'text-inherit',
        'shadow-xl',
        'animate-fade-in backdrop:animate-backdrop-fade-in backdrop:bg-black/50',
        maxWidth,
        className,
      )}
    >
      <div className={cn('flex-shrink-0 sticky top-0 z-10 bg-secondary-50', headerClassName)}>
        <div className="flex justify-end p-4 pb-0 md:px-8">
          <ButtonIcon
            iconName="CloseBtn"
            iconSize={16}
            size="md"
            variant="icon"
            className={cn(
              'cursor-pointer text-secondary-300 transition-colors duration-200 hover:text-secondary-400',
              closeButtonClassName,
            )}
            onClick={onClose}
            aria-label={closeButtonAriaLabel}
          />
        </div>
        {title !== undefined && (
          <div className="px-4 md:px-8 pb-2">
            <Typography variant={titleVariant} className="mb-1 text-primary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="bodySm" as="p">
                {subtitle}
              </Typography>
            )}
          </div>
        )}
      </div>
      {children}
    </dialog>
  );
}

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
